import Polygon from "@arcgis/core/geometry/Polygon";
import { TableRow } from "../interfaces/krookie";
import Polyline from "@arcgis/core/geometry/Polyline";
import {
  contains,
  planarArea,
  planarLength,
} from "@arcgis/core/geometry/geometryEngine";
import {
  Contours,
  LandPlotsCoordinatesBlock,
  Points,
} from "../services/reducers/master-plan-modification";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { v4 as uuidv4 } from "uuid";
import { CheckForErrorsFunction } from "../shared/coordinates-block/coordinates-block";

const spatialMap = new Map([
  [32640, "WGS 84 / UTM zone 40N"],
  [32639, "WGS 84 / UTM zone 39N"],
]);
export const getSpatialByWkid = (wkid?: number): string => {
  return wkid
    ? spatialMap.has(wkid)
      ? spatialMap.get(wkid)!
      : "Unsupported"
    : "";
};

export const ConvertToLines = (polygon: Polygon | null) => {
  if (!polygon) return null;
  const rows: TableRow[] = [];
  let index = 1;
  for (const ring of polygon.rings) {
    ring.forEach((item: number[], idx) => {
      if (idx === ring.length - 1) return;
      const endIndex = idx === ring.length - 2 ? index : idx + 1 + index;
      const line = new Polyline({ spatialReference: polygon.spatialReference });
      line.addPath([item, ring[idx + 1]]);
      rows.push({
        line: `${idx + index}-${endIndex}`,
        north: item[0],
        east: item[1],
        distance: planarLength(line, "meters").toFixed(2),
      });
    });
    index += ring.length - 1;
  }
  return rows;
};

export const ConvertToPolygons = (
  data: LandPlotsCoordinatesBlock,
): Map<number, Polygon> => {
  const result = new Map<number, Polygon>();
  data.forEach((plot) => {
    if (!plot.contours.length) return;

    const rings = plot.contours.map((contour) =>
      contour.points
        .filter(
          (p) => !!p.north && !!p.east && !isNaN(+p.north) && !isNaN(+p.east),
        )
        .map((p) => [+p.north!, +p.east!]),
    );

    result.set(
      plot.PlotId,
      new Polygon({
        rings: rings,
        spatialReference: new SpatialReference({
          wkid: plot.wkid,
        }),
      }),
    );
  });
  return result;
};

export const convertPolygonToString = (
  geometry: Polygon | Polyline,
): { rings: string[][][]; wkid: number } => {
  const ringsOrPaths = (geometry as any).rings ?? (geometry as any).paths;

  const rings = ringsOrPaths.map((ring: number[][]) =>
    ring.map((point) => [point[0].toString(), point[1].toString()]),
  );

  return {
    rings,
    wkid: geometry.spatialReference.wkid,
  };
};

export const ConvertPolygonsToCoordinatesTable = (
  data: LandPlotsCoordinatesBlock,
): Map<number, { rings: string[][][]; wkid: number }> => {
  const result = new Map<number, { rings: string[][][]; wkid: number }>();

  data.forEach((plot) => {
    if (!plot.contours.length || !plot.wkid) return;

    const rings: string[][][] = plot.contours.map((contour) =>
      contour.points.map((point) => [
        point.north?.toString() ?? "",
        point.east?.toString() ?? "",
      ]),
    );

    result.set(plot.PlotId, {
      rings,
      wkid: plot.wkid,
    });
  });

  return result;
};

export const convertPlotsToCoordinatesTable = (
  plots: Array<{
    objectId: number;
    number?: string;
    stringGeometry?: {
      rings: string[][][];
      wkid: number;
    };
  }>,
) => {
  return plots.map((plot) => {
    const contours: Contours = [];
    const validRings: number[][][] = [];
    if (plot.stringGeometry) {
      for (const ring of plot.stringGeometry.rings) {
        let points: any = [];
        ring.forEach((item: string[], idx) => {
          const point = {
            index: idx + 1,
            north: item[0],
            east: item[1],
            id: uuidv4(),
          };
          points.push(point);
        });
        const numericRing = ring.map((item) => [
          Number(item[0]),
          Number(item[1]),
        ]);
        const isValidRing = isPolygon(numericRing);
        if (isValidRing) {
          validRings.push(numericRing);
        }
        const area = isValidRing
          ? planarArea(
              new Polygon({
                rings: [numericRing],
                spatialReference: { wkid: plot.stringGeometry.wkid },
              }),
              "square-meters",
            ).toFixed(2)
          : "0";

        contours.push({
          id: uuidv4(),
          points: points,
          area,
        });
      }
    }

    const totalArea =
      validRings.length > 0
        ? planarArea(
            new Polygon({
              rings: validRings,
              spatialReference: { wkid: plot.stringGeometry!.wkid },
            }),
            "square-meters",
          ).toFixed(2)
        : "0";

    return {
      PlotId: plot.objectId,
      contours,
      area: totalArea,
      wkid: plot.stringGeometry?.wkid,
    };
  });
};

export const reorderContours = (
  contours: Contours,
  wkid: number | undefined,
) => {
  const outerContours: typeof contours = [];
  const innerContours: typeof contours = [];

  contours.forEach((contour, i) => {
    const ring = contour.points.map((p) => [+p.north!, +p.east!]);

    if (!isPolygon(ring)) {
      outerContours.push(contour);
      return;
    }

    const polygon = new Polygon({
      rings: [ring],
      spatialReference: { wkid: wkid },
    });

    let isInnerRing = false;

    contours.forEach((otherContour, j) => {
      if (i === j || !otherContour?.points || otherContour.points.length < 4)
        return;

      const otherRing = otherContour.points.map((p) => [+p.north!, +p.east!]);

      const otherPolygon = new Polygon({
        rings: [otherRing],
        spatialReference: { wkid: wkid },
      });

      if (contains(otherPolygon, polygon)) {
        isInnerRing = true;
      }
    });

    const isClockwise = polygon.isClockwise(ring);

    if ((isInnerRing && isClockwise) || (!isInnerRing && !isClockwise)) {
      contour.points.reverse();
    }

    if (isInnerRing) {
      innerContours.push(contour);
    } else {
      outerContours.push(contour);
    }
  });

  return [...outerContours, ...innerContours];
};

export const isPolygon = (
  inputCoordinates: number[][][] | number[][],
): boolean => {
  if (inputCoordinates.length === 0) return false;

  const polygonPaths: number[][][] = Array.isArray(inputCoordinates[0][0])
    ? (inputCoordinates as number[][][])
    : [inputCoordinates as number[][]];

  return polygonPaths.every((path) => {
    if (path.length < 4) return false;
    const [startX, startY] = path[0];
    const [endX, endY] = path[path.length - 1];
    return startX === endX && startY === endY;
  });
};

export const hasValue = (value?: string | null): value is string =>
  typeof value === "string" && value.trim() !== "";

export const normalizeContour = (contourPoints: Points): void => {
  if (contourPoints.length === 0) return;

  const firstPoint = contourPoints[0];
  const lastPoint = contourPoints[contourPoints.length - 1];
  const secondToLastPoint = contourPoints[contourPoints.length - 2];

  const secondToLastClosesRing =
    contourPoints.length >= 3 &&
    secondToLastPoint.north?.trim() === firstPoint.north?.trim() &&
    secondToLastPoint.east?.trim() === firstPoint.east?.trim();

  const lastPointIsEmpty =
    !hasValue(lastPoint.north) && !hasValue(lastPoint.east);
  const lastPointIsComplete =
    hasValue(lastPoint.north) && hasValue(lastPoint.east);

  if (secondToLastClosesRing && lastPointIsEmpty) {
    contourPoints.pop();
    return;
  }

  const lastPointEqualsFirst =
    lastPoint.north?.trim() === firstPoint.north?.trim() &&
    lastPoint.east?.trim() === firstPoint.east?.trim();

  if (
    lastPointIsComplete &&
    (contourPoints.length === 1 || !lastPointEqualsFirst)
  ) {
    contourPoints.push({
      id: uuidv4(),
      index: lastPoint.index + 1,
      north: "",
      east: "",
      err: false,
    });
  }

  while (contourPoints.length >= 2) {
    const currentLast = contourPoints[contourPoints.length - 1];
    const previousLast = contourPoints[contourPoints.length - 2];
    const currentLastIsEmpty =
      !hasValue(currentLast.north) && !hasValue(currentLast.east);
    const previousLastIsEmpty =
      !hasValue(previousLast.north) && !hasValue(previousLast.east);

    if (currentLastIsEmpty && previousLastIsEmpty) {
      contourPoints.pop();
    } else {
      break;
    }
  }
};

export const createEmptyContour = () => {
  return {
    id: uuidv4(),
    area: "0",
    points: [
      {
        id: uuidv4(),
        index: 1,
        north: "",
        east: "",
        err: false,
      },
    ],
  };
};

export const checkForErrors: CheckForErrorsFunction = (data) => {
  let copiedData: LandPlotsCoordinatesBlock = JSON.parse(JSON.stringify(data));
  copiedData.forEach((landPlot) => {
    const pointMap = new Map<
      string,
      Array<{ contourId: string; pointIndex: number }>
    >();

    const generateKey = (north: string | null, east: string | null): string =>
      `${north},${east}`;

    landPlot.contours.forEach((contour) => {
      contour.points.forEach((point, pointIndex) => {
        const key = generateKey(point.north, point.east);

        if (!pointMap.has(key)) {
          pointMap.set(key, []);
        }

        pointMap.get(key)?.push({ contourId: contour.id, pointIndex });
      });
    });

    landPlot.contours.forEach((contour) => {
      contour.points.forEach((point) => {
        const key = generateKey(point.north, point.east);
        const points = pointMap.get(key);

        if (points && points.length > 1) {
          const isClosingPoint = points.some((point) => {
            return (
              point.contourId == contour.id &&
              point.pointIndex == contour.points.length - 1
            );
          });

          if (!isClosingPoint) {
            points.forEach((point) => {
              const targetContour = landPlot.contours.find(
                (c) => c.id == point.contourId,
              );
              if (targetContour) {
                targetContour.points[point.pointIndex].err = true;
              }
            });
          }
        }
      });
    });
  });

  return copiedData;
};

export const trimZeros = (value: string): string => {
  if (value.trim() === "") return value;
  const num = parseFloat(value);
  return !isNaN(num) ? num.toString() : value;
};
